import React, { useCallback, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { useNodes, useEdges } from "@xyflow/react";
import { X } from "lucide-react";
import { NodeDataSidebar } from "./NodeDataSidebar";
import { InputSchemaView } from "./InputSchemaView";
import { OutputPanel } from "./OutputPanel";
import { THEME_COLORS, type ThemeColor } from "./WorkflowBaseNode";

import { AvailableVariable } from "../ui/types";
import { getAllAncestorNodes } from "../../utils/workflowHelpers";

// ─── Props ──────────────────────────────────────────────────────────────────
export interface WorkflowNodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  nodeId: string;
  title: string;
  icon: React.ReactNode;
  themeColor: ThemeColor;
  nodeDocsType?: string;

  // Left sidebar - optional override; auto-computed from ancestors if omitted
  previousNodes?: AvailableVariable[];
  onDragStart?: (
    e: React.DragEvent,
    variable: string,
    nodeName: string,
    type: "input" | "output",
  ) => void;

  // Center content
  validationStatus: { isValid: boolean; errors: string[] };
  children: React.ReactNode;

  // Footer
  saveDisabled?: boolean;
  saveLabel?: string;
  onSave?: () => void;

  // Right sidebar (outputs)
  nodeData: any;
  nodeIcon: React.ReactNode;
  emptyOutputsText?: string;

  // Optional: fully custom right sidebar
  renderOutputsSidebar?: () => React.ReactNode;
  // Optional: fully custom left sidebar
  renderLeftSidebar?: () => React.ReactNode;
}

// ─── Component ──────────────────────────────────────────────────────────────
export const WorkflowNodeModal: React.FC<WorkflowNodeModalProps> = ({
  isOpen,
  onClose,
  nodeId,
  title,
  icon,
  themeColor,
  nodeDocsType,
  previousNodes: previousNodesProp,
  onDragStart: onDragStartProp,
  validationStatus,
  children,
  saveDisabled = false,
  saveLabel = "Save Configuration",
  onSave,
  nodeData,
  nodeIcon,
  emptyOutputsText = "No outputs yet",
  renderOutputsSidebar,
  renderLeftSidebar,
}) => {
  const theme = THEME_COLORS[themeColor];
  const nodes = useNodes();
  const edges = useEdges();

  // ── Auto-compute ancestor nodes if not provided ───────────────────
  const previousNodes = useMemo(() => {
    if (previousNodesProp) return previousNodesProp;
    const ancestors = getAllAncestorNodes(nodeId, nodes, edges);
    return ancestors.map((n) => ({
      nodeId: n.id,
      nodeName: n.data?.label || n.data?.name || n.id,
      nodeType: n.type || "unknown",
      inputs: n.data?.inputs || {},
      outputs: n.data?.outputs || {},
    })) as AvailableVariable[];
  }, [previousNodesProp, nodeId, nodes, edges]);

  // ── Default drag start handler ────────────────────────────────────
  const defaultDragStart = useCallback(
    (
      e: React.DragEvent,
      variable: string,
      nodeName: string,
      type: "input" | "output",
    ) => {
      e.stopPropagation();
      const fullVariable =
        type === "output"
          ? variable
            ? `{{${nodeName}.outputs.${variable}}}`
            : `{{${nodeName}.outputs}}`
          : variable
            ? `{{${nodeName}.inputs.${variable}}}`
            : `{{${nodeName}.inputs}}`;
      e.dataTransfer.effectAllowed = "copy";
      e.dataTransfer.setData("text/plain", fullVariable);
    },
    [],
  );

  const onDragStart = onDragStartProp || defaultDragStart;

  const [leftView, setLeftView] = useState<"json" | "schema">("json");

  const stopPropagation = useCallback(
    (e: React.MouseEvent | React.FocusEvent) => e.stopPropagation(),
    [],
  );

  const handleSave = useCallback(() => {
    (onSave || onClose)();
  }, [onSave, onClose]);

  if (!isOpen) return null;

  const hasOutputs =
    nodeData?.outputs && Object.keys(nodeData.outputs).length > 0;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black/70 z-[9999] flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-md shadow-xl w-[97vw] h-[94vh] overflow-hidden flex flex-col"
        onClick={stopPropagation}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-3 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className={`p-1 ${theme.iconBg}`}>{icon}</div>
            <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
          </div>
          <div className="flex items-center gap-3">
            {nodeDocsType && (
              <a
                href={`/docs/${nodeDocsType}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:underline transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                Docs
              </a>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Three-column layout */}
        <div className="flex-1 overflow-hidden flex divide-x divide-gray-200">
          {/* Left sidebar - Input Data / Previous Nodes */}
          {renderLeftSidebar ? (
            renderLeftSidebar()
          ) : (
            <aside className="w-[340px] shrink-0 bg-gray-50/50 flex flex-col h-full overflow-hidden">
              <div className="px-4 py-2 border-b border-gray-200 flex items-center gap-1">
                <button
                  onClick={() => setLeftView("json")}
                  className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded transition-colors ${
                    leftView === "json"
                      ? "bg-gray-900 text-white"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Table
                </button>
                <button
                  onClick={() => setLeftView("schema")}
                  className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded transition-colors ${
                    leftView === "schema"
                      ? "bg-gray-900 text-white"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Schema
                </button>
              </div>
              <div className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-300">
                {leftView === "json" ? (
                  <NodeDataSidebar
                    availableVariables={previousNodes}
                    onDragStart={onDragStart}
                  />
                ) : (
                  <InputSchemaView
                    availableVariables={previousNodes}
                    onDragStart={onDragStart}
                  />
                )}
              </div>
            </aside>
          )}

          {/* Center - Configuration / Settings */}
          <main className="flex-1 min-w-[400px] overflow-y-auto bg-white [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-300">
            <div className="px-4 py-3 h-full w-full">
              {children}
            </div>
          </main>

          {/* Right sidebar - Output Preview */}
          {renderOutputsSidebar ? (
            renderOutputsSidebar()
          ) : (
            <aside className="w-[380px] shrink-0 bg-gray-50/50 flex flex-col h-full overflow-hidden">
              <OutputPanel
                hasOutputs={hasOutputs}
                outputs={nodeData?.outputs}
                nodeIcon={nodeIcon}
                emptyOutputsText={emptyOutputsText}
              />
            </aside>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-3 py-3 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            {validationStatus.isValid ? (
              <div className="flex items-center gap-2 text-sm text-green-700">
                <div className="w-2 h-2 bg-green-500 rounded-full" />
                <span className="font-medium">Configuration Valid</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-red-700">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                <span className="font-medium">
                  {validationStatus.errors.length} issue(s) found
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!validationStatus.isValid || saveDisabled}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${theme.buttonBg} ${theme.buttonHover}`}
            >
              {saveLabel}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default WorkflowNodeModal;
